import { useEffect, useRef } from 'react';

import { onBackButton } from './telegram';

/**
 * The way out of a pushed screen, and there is only one of it.
 *
 * Telegram draws the arrow in its own header and routes the phone's back key to
 * it; the app used to draw a second arrow of its own under that one, in a
 * `BackBar` that also said where it led. Both at once is what the owner saw on
 * 2026-08-31, and the answer of that date is the client's and not ours. What is
 * given up is the name of the destination — «‹ Хор» against a bare chevron —
 * and the price is paid once per screen rather than argued about per screen.
 *
 * So a pushed screen holds the key and draws nothing. Passing `null` is a
 * screen that is not pushed at the moment: `ChoirScreen` is its own hub on one
 * page and the table of attendance on another, and only the second is a screen
 * somebody arrived at.
 *
 * Never `history.back()`: an entry pushed inside an iframe takes the whole
 * client back rather than the screen, and under StrictMode the pop from one
 * mount's cleanup lands in the next mount's listener. `Sheet` settled that
 * first and this follows it.
 */
export function useBackKey(onBack: (() => void) | null): void {
  // Read inside a listener attached once, the way `Sheet` reads its `onClose`.
  // The callers write `() => setPage(...)` inline, so a fresh closure arrives
  // on every render and depending on it would hand the key back and forth —
  // which the client sees as an arrow going away and returning.
  const back = useRef(onBack);
  back.current = onBack;

  // Only whether the screen holds it at all, so a screen that starts holding
  // it re-registers and one that merely re-renders does not.
  const held = onBack !== null;
  useEffect(() => {
    if (!held) return;
    return onBackButton(() => back.current?.());
  }, [held]);
}
